import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppBar } from "../../components/AppShell";
import { useToast } from "../../components/Toast";
import { request } from "../../lib/api";
import type { AttemptResult, StartAttemptResponse } from "../../lib/types";

export function Exam() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [attempt, setAttempt] = useState<StartAttemptResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selected, setSelected] = useState<Map<string, Set<string>>>(new Map());
  const [remainingMs, setRemainingMs] = useState<number>(0);

  useEffect(() => {
    if (!courseId) return;
    request<StartAttemptResponse>(`/courses/${courseId}/exam/start`, { method: "POST" })
      .then(setAttempt)
      .catch((e) => setError(e instanceof Error ? e.message : "Eroare"));
  }, [courseId]);

  useEffect(() => {
    if (!attempt) return;
    const expires = new Date(attempt.expires_at).getTime();
    const tick = () => setRemainingMs(Math.max(0, expires - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [attempt]);

  const exam = attempt?.exam;
  const questions = exam?.questions || [];
  const q = questions[currentIdx];
  const sel = q ? selected.get(q.id) || new Set<string>() : new Set<string>();
  const answeredCount = useMemo(() => Array.from(selected.values()).filter((s) => s.size > 0).length, [selected]);

  function setAnswer(answerId: string) {
    if (!q) return;
    const newSelected = new Map(selected);
    if (q.type === "single" || q.type === "true_false") {
      newSelected.set(q.id, new Set([answerId]));
    } else {
      const cur = new Set(sel);
      if (cur.has(answerId)) cur.delete(answerId);
      else cur.add(answerId);
      newSelected.set(q.id, cur);
    }
    setSelected(newSelected);
  }

  async function submit(skipConfirm = false) {
    if (!attempt) return;
    if (!skipConfirm && !confirm("Trimite examenul? Nu vei mai putea modifica răspunsurile.")) return;
    const answers = questions.map((qq) => ({
      question_id: qq.id,
      selected_answer_ids: Array.from(selected.get(qq.id) || []),
    }));
    try {
      const result = await request<AttemptResult>(`/attempts/${attempt.attempt_id}/submit`, {
        method: "POST",
        body: { answers },
      });
      sessionStorage.setItem("mc.last_result", JSON.stringify({ result, courseId, examTitle: exam!.title }));
      navigate("/student/rezultat");
    } catch (err) {
      toast(err instanceof Error ? err.message : "Eroare la trimitere", "error");
    }
  }

  useEffect(() => {
    if (remainingMs === 0 && attempt) submit(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainingMs, attempt]);

  const m = Math.floor(remainingMs / 60000);
  const s = Math.floor((remainingMs % 60000) / 1000);
  const timerLabel = remainingMs === 0 ? "Timp expirat" : `${m}:${String(s).padStart(2, "0")}`;

  if (error) {
    return (
      <>
        <AppBar crumbs={[{ label: "Examen" }]} />
        <div className="content"><div className="card text-muted">Nu am putut porni examenul: {error}</div></div>
      </>
    );
  }
  if (!attempt || !q) {
    return (
      <>
        <AppBar crumbs={[{ label: "Examen" }]} />
        <div className="content"><div className="card text-muted">Se încarcă examenul…</div></div>
      </>
    );
  }

  const progressPct = Math.round(((currentIdx + 1) / questions.length) * 100);

  return (
    <>
      <AppBar
        crumbs={[{ label: "Cursurile mele", to: "/student/cursuri" }, { label: exam!.title }]}
        rightExtras={<span className="exam-timer"><i className="fa-regular fa-clock"></i> {timerLabel}</span>}
      />
      <div className="content">
        <div className="exam-shell">
          <div className="page-head" style={{ marginBottom: 16 }}>
            <div>
              <h1 style={{ fontSize: "1.5rem", marginBottom: 4 }}>{exam!.title}</h1>
              <p>Răspunde la toate întrebările. Trimite după ce ai terminat.</p>
            </div>
          </div>

          <div className="exam-progress">
            <strong style={{ fontSize: ".9rem" }}>Întrebarea {currentIdx + 1} din {questions.length}</strong>
            <div className="exam-progress-bar"><span style={{ width: `${progressPct}%` }}></span></div>
            <span style={{ fontSize: ".85rem", color: "var(--c-muted)" }}>{answeredCount} răspunsuri</span>
          </div>

          <div className="exam-question">
            <span className="qnum">Întrebarea {currentIdx + 1}</span>
            <h2>{q.text}</h2>
            {q.answers.map((a, i) => (
              <div
                key={a.id}
                className={"exam-answer" + (sel.has(a.id) ? " selected" : "")}
                onClick={() => setAnswer(a.id)}
              >
                <div className="opt">{String.fromCharCode(65 + i)}</div>
                <div>{a.text}</div>
              </div>
            ))}
            <div className="exam-footer">
              <button className="btn btn-ghost" disabled={currentIdx === 0} onClick={() => setCurrentIdx(currentIdx - 1)}>
                <i className="fa-solid fa-arrow-left"></i> Anterior
              </button>
              {currentIdx === questions.length - 1 ? (
                <button className="btn btn-accent" onClick={() => submit()}>
                  Trimite examenul <i className="fa-solid fa-paper-plane"></i>
                </button>
              ) : (
                <button className="btn btn-primary" onClick={() => setCurrentIdx(currentIdx + 1)}>
                  Următoarea <i className="fa-solid fa-arrow-right"></i>
                </button>
              )}
            </div>
          </div>

          <div className="card mt-24">
            <h3 style={{ fontSize: ".95rem" }}>Navigator întrebări</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(40px,1fr))", gap: 8 }}>
              {questions.map((qq, idx) => {
                const has = (selected.get(qq.id) || new Set()).size > 0;
                const isCur = idx === currentIdx;
                const styleExtra = isCur
                  ? { background: "var(--c-primary)", color: "#fff" }
                  : has
                    ? { background: "#e6f6ee", color: "#0b6b46", border: "1px solid #b8e0c9" }
                    : {};
                return (
                  <div
                    key={qq.id}
                    style={{ width: "auto", height: 42, borderRadius: 8, display: "grid", placeItems: "center", fontWeight: 700, fontSize: ".85rem", cursor: "pointer", ...styleExtra }}
                    onClick={() => setCurrentIdx(idx)}
                  >
                    {idx + 1}
                  </div>
                );
              })}
            </div>
            <div className="flex flex-between mt-24" style={{ paddingTop: 8, borderTop: "1px solid var(--c-line-2)" }}>
              <small className="muted">{answeredCount} răspunsuri din {questions.length}</small>
              <button className="btn btn-accent btn-sm" onClick={() => submit()}>
                <i className="fa-solid fa-paper-plane"></i> Trimite acum
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
